import { Level } from './level';

/**
 * The school paddock's layout, shared by the view (src/view/paddock.ts, world/schoolPaddock.ts draw it) and the sim
 * (src/sim/obstacles.ts collides with it), so what the rider sees is what the bike hits.
 *
 * Pit coordinates: `a` across (away from the track; negative a is the track side), `b` along the pit, `yaw` about +y
 * on top of the pit's own heading. Collider boxes are in the object's own frame: `w` x `d` footprint (local x, z),
 * `h` tall, centred at (cx, cz).
 */
export interface PitFrame { ox: number; oz: number; l: { x: number; z: number }; f: { x: number; z: number }; yawAlong: number; side: 1 | -1 }
export interface PaddockItem { id: string; a: number; b: number; yaw: number; box?: { w: number; d: number; h: number; cx?: number; cz?: number } }

/** Which side of the default start the pit is on (right of it; the UI's branding sits on the left). */
export const PADDOCK_SIDE = -1 as const;

/** Whether the level has a paddock at all (as the view decides). */
export const hasPaddock = (level: Level): boolean => !!level.starts.default && (!level.def.forest || !!level.def.paddock);

/** The pit's origin and axes in the world. */
export function pitFrame(level: Level, side: 1 | -1 = PADDOCK_SIDE): PitFrame {
  const s = level.starts.default!;
  let f = { x: Math.sin(s.heading), z: Math.cos(s.heading) };
  let l = { x: Math.cos(s.heading) * side, z: -Math.sin(s.heading) * side };
  const off = (level.line ? level.halfWidth : 0) + 11;
  let ox = s.x + l.x * off - f.x * 5, oz = s.z + l.z * off - f.z * 5;
  // A level can say where its paddock stands (level.def.paddock, facing the riding area): centre the pit there with
  // the track side (negative a) toward its heading.
  const pd = level.def.paddock;
  if (pd) {
    l = { x: -Math.sin(pd.heading), z: -Math.cos(pd.heading) };
    f = { x: -l.z / side, z: l.x / side };
    ox = pd.x; oz = pd.z;
  }
  return { ox, oz, l, f, yawAlong: Math.atan2(f.x, f.z), side };
}

/** World (x, z) of pit coordinates (a, b). */
export const pitAt = (p: PitFrame, a: number, b: number) => ({ x: p.ox + p.l.x * a + p.f.x * b, z: p.oz + p.l.z * a + p.f.z * b });

/** Yaw that turns an object's local +z toward the track (negative a). */
export const towardTrack = (side: 1 | -1 = PADDOCK_SIDE) => (-side * Math.PI) / 2;

/** The school paddock (world/schoolPaddock.ts): everything a bike can run into. */
export function schoolPaddock(side: 1 | -1 = PADDOCK_SIDE): PaddockItem[] {
  const tt = towardTrack(side);
  return [
    // Box truck on the LCT chassis, across the pit, rear toward the bikes: 2.3 x ~7 m with the cab, 3.2 m tall.
    { id: 'truck', a: 7.8, b: -1.5, yaw: -tt, box: { w: 2.3, d: 7.2, h: 3.2, cz: 0.7 } },
    // Bikes on stands in a row, fronts toward the track.
    ...Array.from({ length: 8 }, (_, i): PaddockItem => ({ id: `bike${i}`, a: -0.9, b: -7.2 + i * 2.05, yaw: tt + (i % 3 - 1) * 0.06, box: { w: 0.4, d: 2.1, h: 1.2 } })),
    { id: 'plastic_jerrycan', a: 4.6, b: -3.6, yaw: 0.3, box: { w: 0.2, d: 0.42, h: 0.35 } },
    { id: 'plastic_jerrycan', a: 4.2, b: -3.9, yaw: -0.4, box: { w: 0.2, d: 0.42, h: 0.35 } },
    { id: 'plastic_jerrycan', a: 4.9, b: -4.1, yaw: 1.2, box: { w: 0.2, d: 0.42, h: 0.35 } },
    { id: 'metal_jerrycan', a: 5.2, b: -3.3, yaw: 0.1, box: { w: 0.2, d: 0.45, h: 0.35 } },
    { id: 'plastic_container', a: 4.7, b: 1.8, yaw: 0.1, box: { w: 0.6, d: 0.4, h: 0.72 } },
    { id: 'plastic_container', a: 4.6, b: 2.6, yaw: 0.2, box: { w: 0.6, d: 0.4, h: 0.36 } },
    ...([[2.6, 4.2, 0.4], [3.4, 5.4, 0.9], [2.2, 6.4, 1.6], [3.8, 3.2, -0.3]] as const).map(([a, b, yaw]): PaddockItem => ({ id: 'chair', a, b, yaw, box: { w: 0.55, d: 0.55, h: 0.85 } })),
    { id: 'folding_chair', a: 5.2, b: 4.4, yaw: 2.6, box: { w: 0.5, d: 0.5, h: 0.82 } },
    { id: 'folding_chair', a: 5.9, b: 3.7, yaw: 3.3, box: { w: 0.5, d: 0.5, h: 0.82 } },
  ];
}

/** Collider boxes of the paddock in the world: centre, half extents, height, yaw. */
export function paddockBoxes(level: Level): { x: number; z: number; hx: number; hz: number; h: number; yaw: number }[] {
  if (!hasPaddock(level)) return [];
  const p = pitFrame(level);
  return schoolPaddock(p.side).filter((it) => it.box).map((it) => {
    const b = it.box!, yy = p.yawAlong + it.yaw, c = Math.cos(yy), s = Math.sin(yy);
    const q = pitAt(p, it.a, it.b), cx = b.cx ?? 0, cz = b.cz ?? 0;
    return { x: q.x + c * cx + s * cz, z: q.z - s * cx + c * cz, hx: b.w / 2, hz: b.d / 2, h: b.h, yaw: yy };
  });
}
